import { RefreshCw, RotateCcw, Share2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SkeletonRows } from "@/components/ui/skeleton";
import { StudioButton } from "@/features/studio/theme";
import {
	usePendingRevokeIds,
	useProjectShareHistory,
	useRevokeShareLink,
} from "../hooks/use-share-history";
import { getArtifactLabel } from "../services/share-message.service";

interface ShareHistoryProps {
	projectId: string;
}

export function ShareHistory({
	projectId,
}: ShareHistoryProps): React.ReactElement {
	const { t, i18n } = useTranslation();
	const historyQuery = useProjectShareHistory(projectId);
	const revokeMutation = useRevokeShareLink(projectId);
	// A failed load shows the empty state, not an error.
	const history = historyQuery.data?.shareLinks ?? [];
	const pendingRevokeIds = usePendingRevokeIds(projectId);

	const handleRevoke = (shareLinkId: string): void => {
		revokeMutation.mutate(shareLinkId);
	};

	return (
		<div
			className="rounded-2xl"
			style={{
				background: "var(--color-dashboard-paper)",
				border: "1px solid var(--color-dashboard-border)",
			}}
		>
			<div
				className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center sm:justify-between"
				style={{ borderBottom: "1px solid var(--color-dashboard-border)" }}
			>
				<div>
					<div className="flex items-center gap-2">
						<Share2
							className="h-5 w-5"
							style={{ color: "var(--color-dashboard-accent-dark)" }}
						/>
						<span
							className="text-lg"
							style={{
								fontFamily: "var(--font-display)",
								fontStyle: "italic",
								color: "var(--color-dashboard-text)",
							}}
						>
							{t("sharing.history")}
						</span>
					</div>
					<p
						className="text-sm"
						style={{ color: "var(--color-dashboard-muted)" }}
					>
						{t("sharing.historyDescription")}
					</p>
				</div>
				<StudioButton
					type="button"
					variant="secondary"
					size="sm"
					icon={RefreshCw}
					onClick={() => void historyQuery.refetch()}
				>
					{t("sharing.refresh")}
				</StudioButton>
			</div>
			<div className="p-6">
				{historyQuery.isFetching ? (
					<SkeletonRows label={t("sharing.loadingShared")} rows={2} />
				) : history.length === 0 ? (
					<p
						className="text-sm"
						style={{ color: "var(--color-dashboard-muted)" }}
					>
						{t("sharing.noHistory")}
					</p>
				) : (
					<div className="space-y-3">
						{history.map((link) => (
							<div
								key={link.id}
								className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between"
								style={{ borderColor: "var(--color-dashboard-border)" }}
							>
								<div>
									<p
										className="text-sm font-medium"
										style={{ color: "var(--color-dashboard-text)" }}
									>
										{getArtifactLabel(link.artifactType)}: {link.summary.title}
									</p>
									<p
										className="text-xs"
										style={{ color: "var(--color-dashboard-muted)" }}
									>
										{link.deliveryChannel} ·{" "}
										{link.latestEvent?.eventType ?? "created"} ·{" "}
										{new Date(link.createdAt).toLocaleString(i18n.language)}
									</p>
								</div>
								<StudioButton
									type="button"
									variant="secondary"
									size="sm"
									icon={RotateCcw}
									disabled={
										!!link.revokedAt || pendingRevokeIds.includes(link.id)
									}
									onClick={() => handleRevoke(link.id)}
								>
									{link.revokedAt ? t("sharing.revoked") : t("sharing.revoke")}
								</StudioButton>
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
}
